import { useEffect, useRef } from 'react';
import RestoApiService, { CityBoundsResponse } from '../utils/RestoApiService';

interface UseCityBoundsProps {
  map: google.maps.Map | null;
  restoApiService: RestoApiService;
}

/**
 * Hook to handle city parameter in URL and set initial map bounds
 * 
 * This hook checks for a 'city' parameter in the URL on initial load and
 * fetches the city bounds from the backend API to set the map view.
 * 
 * Note: If both 'city' and 'restoId' parameters are present in the URL,
 * the city bounds will be set first, then the restoId handling in MapWrapper
 * will zoom to the specific restaurant. This allows showing a restaurant
 * within the context of a city.
 */
export function useCityBounds({ map, restoApiService }: UseCityBoundsProps) {
  // Ref to track if we've already processed the city parameter
  const cityProcessed = useRef(false);

  useEffect(() => {
    // Only run if map is ready and we haven't processed yet
    if (!map || cityProcessed.current) {
      return;
    }

    // Get the city from URL - either ?city= query param or /stad/{city} path
    const urlParams = new URLSearchParams(window.location.search);
    let cityName = urlParams.get('city');

    // Also check path-based city route
    if (!cityName) {
      const pathMatch = window.location.pathname.match(/\/(?:stad|ville|city)\/([^/?#]+)/);
      if (pathMatch) {
        cityName = decodeURIComponent(pathMatch[1]);
      }
    }

    // If no city found, nothing to do
    if (!cityName) {
      return;
    }

    // Mark as processed immediately to prevent re-runs
    cityProcessed.current = true;

    // Async function to fetch and set city bounds
    const fetchAndSetCityBounds = async () => {
      try {        
        const result = await restoApiService.fetchCityBounds(cityName);
        
        if (result.success && result.data) {
          const cityData: CityBoundsResponse = result.data;
          
          // Create Google Maps LatLngBounds from the city bounds
          const bounds = new google.maps.LatLngBounds();
          
          // Add all four corners to the bounds - API uses latitude/longitude
          bounds.extend(new google.maps.LatLng(cityData.bounds.topLeft.latitude, cityData.bounds.topLeft.longitude));
          bounds.extend(new google.maps.LatLng(cityData.bounds.topRight.latitude, cityData.bounds.topRight.longitude));
          bounds.extend(new google.maps.LatLng(cityData.bounds.bottomRight.latitude, cityData.bounds.bottomRight.longitude));
          bounds.extend(new google.maps.LatLng(cityData.bounds.bottomLeft.latitude, cityData.bounds.bottomLeft.longitude));
          
          // Fit the map to these bounds
          map.fitBounds(bounds);
                    
          // Optional: Add some padding to the bounds for better visual appearance
          const padding = { top: 50, right: 50, bottom: 50, left: 50 };
          map.fitBounds(bounds, padding);
        } else {
          console.error('Failed to fetch city bounds:', result.error);
        }
      } catch (error) {
        console.error('Error fetching city bounds:', error);
      }
    };

    // Execute the async function
    fetchAndSetCityBounds();
  }, [map, restoApiService]);
}